import type { PlaneFrame, PlaneVec2, PlaneVec3 } from '../../store/reducers/measurementPlanes.reducer.ts';

const sub = (a: PlaneVec3, b: PlaneVec3): PlaneVec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: PlaneVec3, b: PlaneVec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: PlaneVec3, b: PlaneVec3): PlaneVec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const normalize = (a: PlaneVec3): PlaneVec3 => {
  const l = Math.hypot(a[0], a[1], a[2]) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
};

/** A surface counts as vertical (façade) when its normal is far from world up. */
export const isVerticalNormal = (n: PlaneVec3): boolean => Math.abs(normalize(n)[2]) < 0.5;

/**
 * Build an oriented frame from a hit point + surface normal: `u` is the horizontal
 * axis along the wall, `v` points up the wall, `normal` faces outward.
 */
export function makeFrame(origin: PlaneVec3, normal: PlaneVec3): PlaneFrame {
  const n = normalize(normal);
  let u = cross(n, [0, 0, 1]);
  if (Math.hypot(u[0], u[1], u[2]) < 1e-6) u = [1, 0, 0]; // degenerate (n ~ vertical)
  u = normalize(u);
  const v = normalize(cross(u, n));
  return { origin, normal: n, u, v };
}

/** World point → (u, v) in the frame. */
export function projectToFrame(p: PlaneVec3, f: PlaneFrame): PlaneVec2 {
  const d = sub(p, f.origin);
  return [dot(d, f.u), dot(d, f.v)];
}

/** (u, v) in the frame → world point. */
export function frameToWorld(uv: PlaneVec2, f: PlaneFrame): PlaneVec3 {
  return [
    f.origin[0] + uv[0] * f.u[0] + uv[1] * f.v[0],
    f.origin[1] + uv[0] * f.u[1] + uv[1] * f.v[1],
    f.origin[2] + uv[0] * f.u[2] + uv[1] * f.v[2],
  ];
}
